/**
 * Number, date and time forms for Beranda (DNA typography rules): thousands with a dot
 * ("2.990"), "92 %" with a no-break space, "28 Sep 2026" in tables, "10.42" for times,
 * "Kemarin 16.05" and "27 Sep 16.05" for older activity rows (CONTENT.md section 3).
 * Pure functions; dates and times are read in WIT whatever the device zone (dates/wit).
 */

import type { KelompokBeranda, uiStrings } from '@satu/shared'
import { jamWit, samaHariWit, tahunWit, waktuWit } from '@satu/shared/dates/wit'

const angkaId = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 })

const BULAN_PENDEK = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mei',
  'Jun',
  'Jul',
  'Agu',
  'Sep',
  'Okt',
  'Nov',
  'Des',
] as const

export function formatAngka(n: number): string {
  return angkaId.format(n)
}

/** "92 %"; an em dash when there is nothing to measure. */
export function formatPersen(pct: number | null): string {
  return pct === null ? '—' : `${pct} %`
}

/** "10.42": hours and minutes with the Indonesian dot, in WIT. */
export function formatJam(date: Date): string {
  return jamWit(date)
}

function parse(iso: string | null): Date | null {
  if (iso === null) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "28 Sep" or, when `tahun` is set or the year differs from today's, "28 Sep 2026". */
export function formatTanggalPendek(
  iso: string | null,
  now: Date,
  options: { tahun?: boolean } = {},
): string {
  const date = parse(iso)
  if (date === null) return '—'
  const w = waktuWit(date)
  if (w === null) return '—'
  const bulan = BULAN_PENDEK[w.bulan - 1] ?? ''
  const withYear = options.tahun === true || w.tahun !== tahunWit(now)
  return withYear ? `${w.hari} ${bulan} ${w.tahun}` : `${w.hari} ${bulan}`
}

/** Tables always carry the year: "28 Sep 2026" (DNA typography rules). */
export function formatTanggalTabel(iso: string | null, now: Date): string {
  return formatTanggalPendek(iso, now, { tahun: true })
}

/**
 * Activity time: "10.42" today, "Kemarin 16.05" yesterday, "27 Sep 16.05" earlier this
 * year, "27 Sep 2025 16.05" in another year.
 */
export function formatWaktuAktivitas(iso: string, now: Date): string {
  const date = parse(iso)
  if (date === null) return '—'
  const jam = formatJam(date)
  if (samaHariWit(date, now)) return jam
  if (samaHariWit(date, now.getTime() - 24 * 60 * 60 * 1000)) return `Kemarin ${jam}`
  return `${formatTanggalPendek(iso, now)} ${jam}`
}

/** "Maria Kelanit" becomes "Maria K."; a single name stays as it is. */
export function namaPendek(nama: string): string {
  const parts = nama
    .trim()
    .split(/\s+/)
    .filter((p) => p !== '')
  const first = parts[0]
  if (first === undefined) return nama
  const second = parts[1]
  if (second === undefined) return first
  if (/^[A-Za-z]\.$/.test(second)) return `${first} ${second}`
  return `${first} ${second.charAt(0).toUpperCase()}.`
}

/** The Umat per lingkungan (per paroki) chart's caption, its group column and its count column. */
export interface UmatPerKelompokTeks {
  judul: string
  kolom: string
  umat: string
}

/**
 * The chart's words from Beranda's strings as the parish says its unit: the heading, the hidden
 * table a screen reader and the page text read, and the tooltip ("Umat per kombas", "Kombas"),
 * never a hard-coded "lingkungan" (final review 2, Minor 1).
 */
export function teksUmatPerKelompok(
  teks: typeof uiStrings.beranda,
  kelompok: KelompokBeranda,
): UmatPerKelompokTeks {
  return kelompok === 'paroki'
    ? {
        judul: teks.grafik.umatPerParoki,
        kolom: teks.grafik.kolomParoki,
        umat: teks.grafik.kolomUmat,
      }
    : {
        judul: teks.grafik.umatPerLingkungan,
        kolom: teks.kelengkapan.kolom.lingkungan,
        umat: teks.grafik.kolomUmat,
      }
}
